import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatPrice, formatDateTime, localized, apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Trash2, Pencil, Plus, ExternalLink, Truck, PackageCheck, CheckCircle2, AlertCircle, Settings } from "lucide-react";
import { VendorEditDialog } from "@/components/VendorEditDialog";
import { HeroPhotoTipsCard } from "@/components/HeroPhotoTipsCard";

const TYPES = ["extra_virgin", "virgin", "flavored", "organic", "blended"];
const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "Other"];

const blank = {
  title: "", title_hr: "", description: "", description_hr: "",
  price: 0, stock: 0, type: "extra_virgin", region: "", region_hr: "",
  volume_ml: 500, organic: false, certifications: "", images: [],
};

export default function VendorDashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const fileRef = useRef(null);

  // Ship dialog
  const [shipOpen, setShipOpen] = useState(false);
  const [shippingOrder, setShippingOrder] = useState(null);
  const [shipForm, setShipForm] = useState({ tracking_number: "", carrier: "UPS", notes: "" });

  const load = async () => {
    try { setVendor((await api.get("/vendors/me")).data); } catch { setVendor(null); }
    try { setProducts((await api.get("/products/vendor/me")).data); } catch { setProducts([]); }
    try { setOrders((await api.get("/orders/vendor")).data); } catch { setOrders([]); }
    try { setStats((await api.get("/analytics/vendor")).data); } catch { setStats(null); }
    try { setStripeStatus((await api.get("/vendors/stripe/status")).data); } catch { setStripeStatus(null); }
    try { setPayouts((await api.get("/payouts/vendor")).data); } catch { setPayouts(null); }
  };
  useEffect(() => { load(); }, []);

  if (!vendor) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="no-vendor">
        <p className="overline">{t("vendor.overline")}</p>
        <h1 className="font-serif text-4xl mt-4">{t("vendor.noProfile")}</h1>
        <p className="text-ink-soft mt-3">{t("vendor.applyToStart")}</p>
        <a href="/become-vendor" className="inline-block mt-8 bg-olive text-bone px-8 py-4 rounded-sm text-sm">{t("vendor.applyNow")}</a>
      </div>
    );
  }
  if (vendor.status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="vendor-pending">
        <p className="overline">{t("vendor.pendingReview")}</p>
        <h1 className="font-serif text-4xl mt-4">{t("vendor.pendingTitle")}</h1>
        <p className="text-ink-soft mt-3">{t("vendor.pendingBody")}</p>
      </div>
    );
  }

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, certifications: (p.certifications || []).join(", "), images: p.images || [] });
    setOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    const payload = {
      ...form,
      price: parseFloat(form.price), stock: parseInt(form.stock), volume_ml: parseInt(form.volume_ml),
      certifications: form.certifications.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/products/${editing.product_id}`, payload);
      else await api.post("/products", payload);
      setOpen(false); load();
      toast.success(editing ? "Updated" : "Product added");
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    try { await api.delete(`/products/${id}`); load(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  const stripeOnboard = async () => {
    try {
      const { data } = await api.post("/vendors/stripe/onboard");
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    }
  };

  const openShipDialog = (order) => {
    setShippingOrder(order);
    setShipForm({ tracking_number: "", carrier: "UPS", notes: "" });
    setShipOpen(true);
  };

  const submitShip = async () => {
    if (!shipForm.tracking_number.trim()) return toast.error(t("vendor.trackingRequired"));
    try {
      await api.post(`/orders/${shippingOrder.order_id}/ship`, shipForm);
      setShipOpen(false);
      load();
      toast.success(t("vendor.buyerNotified"));
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    }
  };

  const deliverOrder = async (orderId) => {
    if (!window.confirm("OK?")) return;
    try {
      await api.post(`/orders/${orderId}/deliver`);
      load();
      toast.success(t("vendor.payoutReleased2"));
    } catch { toast.error("Failed"); }
  };

  const vendorStatusFor = (order) => {
    return order.fulfillment_by_vendor?.[vendor.vendor_id] || { status: "pending" };
  };

  // Profile completeness — encourages producers to fill every storefront field.
  // Weights: hero fields like story + cover are worth more; HR translations are bonus.
  const completenessFields = [
    { key: "farm_name",     labelKey: "vendorEdit.farmName",     weight: 1 },
    { key: "farm_name_hr",  labelKey: "vendorEdit.farmName",     weight: 1, suffix: " (HR)" },
    { key: "region",        labelKey: "vendorEdit.region",       weight: 1 },
    { key: "region_hr",     labelKey: "vendorEdit.region",       weight: 1, suffix: " (HR)" },
    { key: "story",         labelKey: "vendorEdit.story",        weight: 2 },
    { key: "story_hr",      labelKey: "vendorEdit.story",        weight: 1, suffix: " (HR)" },
    { key: "cover_image",   labelKey: "vendorEdit.coverImage",   weight: 2 },
    { key: "avatar",        labelKey: "vendorEdit.avatar",       weight: 1 },
    { key: "contact_email", labelKey: "vendorEdit.contactEmail", weight: 1 },
    { key: "website",       labelKey: "vendorEdit.website",      weight: 1 },
    { key: "lat",           labelKey: "vendorEdit.lat",          weight: 1, isNum: true },
  ];
  const completed = completenessFields.filter((f) => {
    const v = vendor[f.key];
    return f.isNum ? (v !== null && v !== undefined && v !== 0) : (v && String(v).trim());
  });
  const totalWeight = completenessFields.reduce((s, f) => s + f.weight, 0);
  const earnedWeight = completed.reduce((s, f) => s + f.weight, 0);
  const completenessPct = Math.round((earnedWeight / totalWeight) * 100);
  const missing = completenessFields.filter((f) => !completed.includes(f));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="vendor-dashboard">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <p className="overline">{t("vendor.overline")}</p>
          <h1 className="font-serif text-5xl tracking-tight mt-2" data-testid="vendor-name">{localized(vendor, "farm_name", lang)}</h1>
          <p className="text-ink-soft text-sm mt-1">{localized(vendor, "region", lang)}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setProfileOpen(true)} variant="outline" className="rounded-sm" data-testid="edit-profile-btn">
            <Settings className="w-4 h-4 mr-2" />{t("vendor.editProfile")}
          </Button>
          <Button onClick={openCreate} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" />{t("vendor.newProduct")}
          </Button>
        </div>
      </div>

      {/* Hero-photo recommendation — dismissable, shows once until producer X's it out */}
      <HeroPhotoTipsCard />

      {/* Profile completeness meter */}
      {completenessPct < 100 && (
        <div className="mt-8 bg-white hairline rounded-sm p-5" data-testid="completeness-meter">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="overline">{t("vendor.completenessTitle")}</p>
              <p className="text-sm text-ink-soft mt-1">
                {t("vendor.completenessHint", { count: completed.length, total: completenessFields.length })}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-2xl text-olive font-medium" data-testid="completeness-pct">{completenessPct}%</span>
              <Button onClick={() => setProfileOpen(true)} variant="outline" size="sm" className="rounded-sm" data-testid="completeness-edit-btn">
                {t("vendor.completenessAction")}
              </Button>
            </div>
          </div>
          <div className="mt-4 h-1.5 bg-boneAlt rounded-full overflow-hidden">
            <div
              className="h-full bg-olive transition-[width] duration-700 ease-out"
              style={{ width: `${completenessPct}%` }}
              data-testid="completeness-bar"
            />
          </div>
          {missing.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="completeness-missing">
              {missing.slice(0, 6).map((f) => (
                <span
                  key={f.key}
                  className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-boneAlt text-ink-muted"
                  data-testid={`missing-${f.key}`}
                >
                  {t(f.labelKey)}{f.suffix || ""}
                </span>
              ))}
              {missing.length > 6 && (
                <span className="text-[11px] uppercase tracking-widest px-2 py-1 text-ink-muted">
                  +{missing.length - 6}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Stripe Connect banner */}
      {stripeStatus && !stripeStatus.charges_enabled && (
        <div className="mt-8 bg-terracotta/10 border border-terracotta/30 rounded-sm p-6 flex items-center justify-between flex-wrap gap-3" data-testid="stripe-connect-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-terracotta mt-0.5" />
            <div>
              <p className="font-medium">{t("vendor.stripeBannerTitle")}</p>
              <p className="text-sm text-ink-soft mt-1">
                {t("vendor.stripeBannerBody")}
                {payouts && ` · ${t("vendor.stripePending", { amount: formatPrice(payouts.summary.pending, lang) })}`}
              </p>
            </div>
          </div>
          <Button onClick={stripeOnboard} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="stripe-onboard-btn">
            <ExternalLink className="w-4 h-4 mr-2" />
            {stripeStatus.onboarded ? t("vendor.stripeContinue") : t("vendor.stripeStart")}
          </Button>
        </div>
      )}
      {stripeStatus && stripeStatus.charges_enabled && (
        <div className="mt-8 bg-olive/10 border border-olive/30 rounded-sm p-4 flex items-center gap-3" data-testid="stripe-ready-banner">
          <CheckCircle2 className="w-5 h-5 text-olive" />
          <p className="text-sm">{t("vendor.stripeReady")}</p>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="vendor-stats">
          {[
            { l: t("vendor.statRevenue"), v: formatPrice(stats.total_revenue, lang), t: "stat-revenue" },
            { l: t("vendor.statOrders"), v: stats.total_orders, t: "stat-orders" },
            { l: t("vendor.statUnits"), v: stats.total_units, t: "stat-units" },
            { l: t("vendor.statProducts"), v: stats.product_count, t: "stat-products" },
          ].map((s) => (
            <div key={s.l} className="bg-white hairline rounded-sm p-6" data-testid={s.t}>
              <p className="overline">{s.l}</p>
              <p className="font-serif text-3xl mt-2">{s.v}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="overview" className="mt-12">
        <TabsList className="bg-transparent border-b border-edge rounded-none h-auto p-0 w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-overview">{t("vendor.tabAnalytics")}</TabsTrigger>
          <TabsTrigger value="products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-products">{t("vendor.tabProducts")} ({products.length})</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-orders">{t("vendor.tabOrders")} ({orders.length})</TabsTrigger>
          <TabsTrigger value="payouts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-payouts">{t("vendor.tabPayouts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-10">
          {stats && stats.by_day.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white hairline rounded-sm p-6" data-testid="chart-revenue">
                <p className="overline">{t("vendor.dailyRevenue")}</p>
                <div className="h-64 mt-4">
                  <ResponsiveContainer><LineChart data={stats.by_day}>
                    <CartesianGrid stroke="#E5E0D8" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#8A8480" fontSize={11} />
                    <YAxis stroke="#8A8480" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #E5E0D8", borderRadius: 4 }} />
                    <Line type="monotone" dataKey="revenue" stroke="#4A5D23" strokeWidth={2} dot={{ fill: "#4A5D23" }} />
                  </LineChart></ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white hairline rounded-sm p-6" data-testid="chart-products">
                <p className="overline">{t("vendor.topProducts")}</p>
                <div className="h-64 mt-4">
                  <ResponsiveContainer><BarChart data={stats.by_product}>
                    <CartesianGrid stroke="#E5E0D8" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#8A8480" fontSize={10} angle={-15} textAnchor="end" height={60} />
                    <YAxis stroke="#8A8480" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #E5E0D8", borderRadius: 4 }} />
                    <Bar dataKey="revenue" fill="#E07A5F" />
                  </BarChart></ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-ink-muted">{t("vendor.noSalesYet")}</p>
          )}
        </TabsContent>

        <TabsContent value="products" className="pt-10">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vendor-products-grid">
            {products.map((p) => (
              <div key={p.product_id} className="bg-white hairline rounded-sm overflow-hidden" data-testid={`vendor-product-${p.product_id}`}>
                <div className="aspect-[4/3] bg-boneAlt overflow-hidden">
                  {p.images?.[0] && <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-4">
                  <p className="overline">{localized(p, "region", lang)}</p>
                  <h3 className="font-serif text-xl mt-1">{localized(p, "title", lang)}</h3>
                  <p className="text-sm text-ink-soft">{formatPrice(p.price, lang)} · {t("vendor.stock")} {p.stock}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => openEdit(p)} className="text-sm flex items-center gap-1 hover:text-olive" data-testid={`edit-${p.product_id}`}>
                      <Pencil className="w-3 h-3" />{t("common.edit")}
                    </button>
                    <button onClick={() => del(p.product_id)} className="text-sm flex items-center gap-1 hover:text-terracotta ml-auto" data-testid={`delete-${p.product_id}`}>
                      <Trash2 className="w-3 h-3" />{t("common.delete")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {products.length === 0 && <p className="text-ink-muted">{t("vendor.noProductsYet")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="pt-10">
          <div className="space-y-4" data-testid="vendor-orders-list">
            {orders.map((o) => {
              const s = vendorStatusFor(o);
              return (
                <div key={o.order_id} className="bg-white hairline rounded-sm p-6" data-testid={`order-${o.order_id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-4">
                    <div>
                      <p className="font-mono text-xs text-ink-muted">{o.order_id}</p>
                      <p className="text-sm mt-2">{o.items.map(i => `${i.quantity}× ${i.title}`).join(" · ")}</p>
                      <p className="text-xs text-ink-muted mt-1">{formatDateTime(o.created_at, lang)}</p>
                      {s.tracking_number && (
                        <p className="text-xs text-olive mt-2" data-testid={`tracking-${o.order_id}`}>
                          {s.carrier} · {s.tracking_number}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="overline">{t("vendor.yourSubtotal")}</p>
                      <p className="font-medium">{formatPrice(o.vendor_subtotal, o.currency === "eur" ? "hr" : "en")}</p>
                      <p className="text-xs uppercase tracking-widest mt-2 text-ink-soft" data-testid={`order-status-${o.order_id}`}>{t(`vendor.status${s.status.charAt(0).toUpperCase()}${s.status.slice(1)}`, s.status)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {s.status === "pending" && (
                      <Button onClick={() => openShipDialog(o)} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid={`ship-${o.order_id}`}>
                        <Truck className="w-4 h-4 mr-2" />{t("vendor.markShipped")}
                      </Button>
                    )}
                    {s.status === "shipped" && (
                      <Button onClick={() => deliverOrder(o.order_id)} variant="outline" className="rounded-sm border-olive text-olive hover:bg-olive hover:text-bone" data-testid={`deliver-${o.order_id}`}>
                        <PackageCheck className="w-4 h-4 mr-2" />{t("vendor.markDelivered")}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {orders.length === 0 && <p className="text-ink-muted">{t("vendor.noOrders")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="payouts" className="pt-10">
          {payouts && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="payouts-summary">
                <div className="bg-white hairline rounded-sm p-6">
                  <p className="overline">{t("vendor.payoutPending")}</p>
                  <p className="font-serif text-3xl mt-2" data-testid="payout-pending">{formatPrice(payouts.summary.pending, lang)}</p>
                  <p className="text-xs text-ink-muted mt-1">{t("vendor.payoutReleased")}</p>
                </div>
                <div className="bg-white hairline rounded-sm p-6">
                  <p className="overline">{t("vendor.payoutPaid")}</p>
                  <p className="font-serif text-3xl mt-2" data-testid="payout-paid">{formatPrice(payouts.summary.paid, lang)}</p>
                </div>
                <div className="bg-white hairline rounded-sm p-6">
                  <p className="overline">{t("vendor.payoutTotal")}</p>
                  <p className="font-serif text-3xl mt-2">{payouts.summary.count}</p>
                </div>
              </div>
              <div className="space-y-2" data-testid="payouts-list">
                {payouts.payouts.length === 0 && <p className="text-ink-muted">{t("vendor.noPayouts")}</p>}
                {payouts.payouts.map((p) => (
                  <div key={p.payout_id} className="bg-white hairline rounded-sm p-4 flex justify-between items-center flex-wrap gap-2" data-testid={`payout-${p.payout_id}`}>
                    <div>
                      <p className="text-xs font-mono text-ink-muted">{p.order_id}</p>
                      <p className="text-sm">{t("vendor.grossMinusCommission", { gross: formatPrice(p.gross_amount, lang), commission: formatPrice(p.commission, lang) })}</p>
                      <p className="text-xs text-ink-muted mt-1">{formatDateTime(p.created_at, lang)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatPrice(p.net_amount, lang)}</p>
                      <p className="text-xs uppercase tracking-widest text-ink-soft">{p.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Product dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{editing ? t("vendor.editProduct") : t("vendor.newProductTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldTitle")}</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-title-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldTitleHr")}</label>
              <input value={form.title_hr || ""} onChange={(e) => setForm({ ...form, title_hr: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-title-hr-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldDescription")}</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-description-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldDescriptionHr")}</label>
              <textarea rows={3} value={form.description_hr || ""} onChange={(e) => setForm({ ...form, description_hr: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-description-hr-input" />
            </div>
            <div>
              <label className="overline">{t("vendor.fieldPrice")}</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-price-input" />
            </div>
            <div>
              <label className="overline">{t("vendor.fieldStock")}</label>
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-stock-input" />
            </div>
            <div>
              <label className="overline">{t("vendor.fieldType")}</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-type-input">
                {TYPES.map((tt) => <option key={tt} value={tt}>{tt.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">{t("vendor.fieldVolume")}</label>
              <input type="number" value={form.volume_ml} onChange={(e) => setForm({ ...form, volume_ml: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-volume-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldRegion")}</label>
              <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-region-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldCerts")}</label>
              <input value={form.certifications} onChange={(e) => setForm({ ...form, certifications: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="product-certs-input" />
            </div>
            <label className="flex items-center gap-2 md:col-span-2 text-sm">
              <input type="checkbox" checked={form.organic} onChange={(e) => setForm({ ...form, organic: e.target.checked })} data-testid="product-organic-input" />
              {t("vendor.fieldOrganic")}
            </label>
            <div className="md:col-span-2">
              <label className="overline">{t("vendor.fieldImages")}</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.images.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden hairline">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-white/90 p-0.5">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 hairline rounded-sm flex items-center justify-center cursor-pointer bg-white hover:bg-boneAlt text-xs text-ink-soft" data-testid="upload-image-label">
                  {uploading ? "…" : "+ Upload"}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="upload-image-input" />
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="dialog-cancel">{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="dialog-save">
              {editing ? t("vendor.saveChanges") : t("vendor.createProduct")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ship dialog */}
      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="ship-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t("vendor.shipTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="overline">{t("vendor.shipCarrier")}</label>
              <select value={shipForm.carrier} onChange={(e) => setShipForm({ ...shipForm, carrier: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="ship-carrier">
                {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">{t("vendor.shipTracking")}</label>
              <input value={shipForm.tracking_number} onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="ship-tracking" />
            </div>
            <div>
              <label className="overline">{t("vendor.shipNotes")}</label>
              <textarea rows={2} value={shipForm.notes} onChange={(e) => setShipForm({ ...shipForm, notes: e.target.value })} className="w-full hairline bg-white px-3 py-2 rounded-sm text-sm mt-1" data-testid="ship-notes" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShipOpen(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={submitShip} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="ship-submit">{t("vendor.shipConfirm")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <VendorEditDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        vendor={vendor}
        endpoint="/vendor/profile"
        allowStatus={false}
        onSaved={async () => {
          try { const { data } = await api.get("/vendors/me"); setVendor(data); } catch {/* noop */}
        }}
      />
    </div>
  );
}
